import React, { useEffect, useMemo, useState } from "react";
import ModalBackdrop from "components/ModalBackdrop";
import {
  DateInput,
  DropdownSelect,
  MultiSelectDropdown,
  NumberInput,
  TextAreaInput,
  TextInput,
} from "components/InputStyled";
import {
  getClassificationLeafValues,
  getClassificationPath,
} from "../lib/hierarchy";

const EMPTY_PROJECT = {
  project_code: "",
  project_name: "",
  description: "",
  status: "proposed",
  total_project_cost: "",
  start_date: "",
  expected_completion_date: "",
  actual_completion_date: "",
  notes: "",
  is_active: true,
};

const STATUS_OPTIONS = [
  ["proposed", "Proposed"],
  ["approved", "Approved"],
  ["in_progress", "In Progress"],
  ["completed", "Completed"],
  ["deferred", "Deferred"],
  ["cancelled", "Cancelled"],
  ["archived", "Archived"],
].map(([value, label]) => ({ value, label }));

export default function ProjectModal({
  project,
  classifications = [],
  initialAssignments = [],
  saving,
  onSave,
  onDelete,
  onClose,
}) {
  const [draft, setDraft] = useState(EMPTY_PROJECT);
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    setDraft({ ...EMPTY_PROJECT, ...(project || {}) });
    const grouped = {};
    initialAssignments.forEach((assignment) => {
      grouped[assignment.classification_id] = [
        ...(grouped[assignment.classification_id] || []),
        assignment.classification_value_id,
      ];
    });
    setAssignments(grouped);
  }, [initialAssignments, project]);

  const change = (field) => (value) => setDraft((current) => ({ ...current, [field]: value }));
  const missingRequiredClassification = useMemo(
    () =>
      classifications.some((classification) => {
        const scope = classification.scopes.find(
          (item) => item.object_type === "project"
        );
        const leafIds = new Set(
          getClassificationLeafValues(classification.values)
            .filter((value) => value.is_active)
            .map((value) => value.id)
        );
        const selectedLeaves = (assignments[classification.id] || []).filter(
          (valueId) => leafIds.has(valueId)
        );
        return scope?.is_required && !selectedLeaves.length;
      }),
    [assignments, classifications]
  );

  return (
    <ModalBackdrop onClose={saving ? undefined : onClose}>
      <form
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-border bg-app p-6 shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(
            draft,
            classifications.map((classification) => {
              const leafIds = new Set(
                getClassificationLeafValues(classification.values)
                  .filter((value) => value.is_active)
                  .map((value) => value.id)
              );
              return {
                classification_id: classification.id,
                classification_value_ids: (
                  assignments[classification.id] || []
                ).filter((valueId) => leafIds.has(valueId)),
              };
            })
          );
        }}
      >
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-brand">
            {project?.id ? "Edit Project" : "New Project"}
          </h2>
          <p className="mt-1 text-sm text-muted">
            Projects exist independently of their debt financing.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <TextInput label="Project Code" value={draft.project_code} onChange={change("project_code")} className="w-full" />
          <TextInput label="Project Name" value={draft.project_name} onChange={change("project_name")} className="w-full" required />
          <DropdownSelect label="Status" value={draft.status} onChange={change("status")} options={STATUS_OPTIONS} className="w-full" required />
          <NumberInput label="Total Project Cost" value={draft.total_project_cost} onChange={change("total_project_cost")} type="currency" className="w-full" />
          <DateInput label="Start Date" value={draft.start_date} onChange={change("start_date")} />
          <DateInput label="Expected Completion" value={draft.expected_completion_date} onChange={change("expected_completion_date")} />
          <DateInput label="Actual Completion" value={draft.actual_completion_date} onChange={change("actual_completion_date")} />
        </div>

        {classifications.length ? (
          <div className="mt-5 rounded-lg border border-border p-4">
            <div className="font-medium text-app">Classifications</div>
            <div className="mt-3 grid min-w-0 gap-4 md:grid-cols-2">
              {classifications.map((classification) => {
                const scope = classification.scopes.find(
                  (item) => item.object_type === "project"
                );
                const options = getClassificationLeafValues(classification.values)
                  .filter((value) => value.is_active)
                  .map((value) => ({
                    value: value.id,
                    label: getClassificationPath(classification.values, value.id),
                  }));
                const selected = assignments[classification.id] || [];
                return scope.allow_multiple ? (
                  <MultiSelectDropdown
                    key={classification.id}
                    label={`${classification.name}${scope.is_required ? " *" : ""}`}
                    values={selected}
                    onChange={(values) =>
                      setAssignments((current) => ({
                        ...current,
                        [classification.id]: values,
                      }))
                    }
                    options={options}
                    className="min-w-0 w-full"
                    menuClassName="min-w-0 max-w-full"
                  />
                ) : (
                  <DropdownSelect
                    key={classification.id}
                    label={classification.name}
                    value={selected[0] || ""}
                    onChange={(value) =>
                      setAssignments((current) => ({
                        ...current,
                        [classification.id]: value ? [value] : [],
                      }))
                    }
                    options={options}
                    placeholder={scope.is_required ? "Select a required value" : "No value"}
                    className="w-full"
                    required={scope.is_required}
                  />
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-4">
          <TextAreaInput label="Description" value={draft.description} onChange={change("description")} rows={3} />
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          <div>
            {project?.id ? (
              <button
                type="button"
                className="btn bg-red-600 text-white hover:bg-red-700"
                onClick={onDelete}
                disabled={saving}
              >
                Delete Project
              </button>
            ) : null}
          </div>
          <div className="flex gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !draft.project_name.trim() || missingRequiredClassification}>
            {saving ? "Saving…" : "Save Project"}
          </button>
          </div>
        </div>
      </form>
    </ModalBackdrop>
  );
}
